import type { ChatMessage } from '@teo/widget-shared';
import { normalizeMediaUrl } from '@teo/widget-shared';
import { maxDeliveryStatus } from './message-read-receipts.js';

const mergeStatus = maxDeliveryStatus;

function mediaUrl(message: ChatMessage): string | undefined {
  return message.mediaUrl?.trim() || message.attachment?.url?.trim() || undefined;
}

function outboundMediaUrl(message: ChatMessage): string | undefined {
  const fromMedia = mediaUrl(message);
  if (fromMedia) return normalizeMediaUrl(fromMedia);

  const text = message.text?.trim();
  if (text && /^https?:\/\//i.test(text)) {
    return normalizeMediaUrl(text);
  }

  return undefined;
}

function isSameOutboundMediaUrl(a: ChatMessage, b: ChatMessage): boolean {
  if (a.role !== 'user' || b.role !== 'user') return false;
  const urlA = outboundMediaUrl(a);
  const urlB = outboundMediaUrl(b);
  return Boolean(urlA && urlB && urlA === urlB);
}

function pickStableOutgoingText(
  previous: ChatMessage,
  next: ChatMessage,
): string {
  const attachment = next.attachment ?? previous.attachment;
  const text = next.text?.trim() ?? '';

  if (!text) {
    return previous.text;
  }

  if (
    attachment?.url &&
    normalizeMediaUrl(text) === normalizeMediaUrl(attachment.url)
  ) {
    return '';
  }

  if (isSameOutboundMediaUrl(previous, { ...next, text })) {
    return attachment?.kind === 'video_embed' ? '' : text;
  }

  return text;
}

function preferStableAttachment(
  previous: ChatMessage,
  next: ChatMessage,
): ChatMessage['attachment'] {
  const previousAttachment = previous.attachment;
  const nextAttachment = next.attachment ?? previousAttachment;
  if (
    previousAttachment?.url &&
    nextAttachment?.url &&
    normalizeMediaUrl(previousAttachment.url) ===
      normalizeMediaUrl(nextAttachment.url)
  ) {
    return previousAttachment;
  }
  return nextAttachment;
}

function isSameMessageContent(a: ChatMessage, b: ChatMessage): boolean {
  return (
    String(a.id) === String(b.id) &&
    a.role === b.role &&
    a.text === b.text &&
    (a.contentType ?? 'text') === (b.contentType ?? 'text') &&
    a.createdAt === b.createdAt &&
    mediaUrl(a) === mediaUrl(b)
  );
}

function findPreviousRow(
  message: ChatMessage,
  prevById: Map<string, ChatMessage>,
  prevByClientId: Map<string, ChatMessage>,
  previous: ChatMessage[],
): ChatMessage | undefined {
  if (message.clientId) {
    const byClient = prevByClientId.get(message.clientId);
    if (byClient) return byClient;
  }

  const byId = prevById.get(String(message.id));
  if (byId) return byId;

  if (message.contentType === 'image') {
    const url = mediaUrl(message);
    if (!url) return undefined;
    return previous.find(
      (row) =>
        row.role === 'user' &&
        row.contentType === 'image' &&
        Boolean(row.clientId) &&
        mediaUrl(row) === url,
    );
  }

  if (message.role === 'user' && message.clientId) {
    const url = outboundMediaUrl(message);
    if (url) {
      return previous.find(
        (row) =>
          row.role === 'user' &&
          row.clientId === message.clientId &&
          isSameOutboundMediaUrl(row, message),
      );
    }
  }

  return undefined;
}

/**
 * Reuse previous row objects when WS sync only bumps delivery status.
 * Prevents React from remounting every user bubble (and reloading images).
 */
export function stabilizeMessageList(
  previous: ChatMessage[],
  next: ChatMessage[],
): ChatMessage[] {
  if (previous.length === 0) return next;

  const prevById = new Map<string, ChatMessage>();
  const prevByClientId = new Map<string, ChatMessage>();

  for (const message of previous) {
    prevById.set(String(message.id), message);
    if (message.clientId) {
      prevByClientId.set(message.clientId, message);
    }
  }

  return next.map((message) => {
    const previousRow = findPreviousRow(
      message,
      prevById,
      prevByClientId,
      previous,
    );

    if (!previousRow) return message;
    if (previousRow === message) return previousRow;

    const sameImageUrl =
      previousRow.contentType === 'image' &&
      message.contentType === 'image' &&
      mediaUrl(previousRow) === mediaUrl(message);

    if (sameImageUrl && previousRow.clientId) {
      const merged: ChatMessage = {
        ...previousRow,
        id: message.id,
        createdAt: message.createdAt,
        status: mergeStatus(previousRow.status, message.status),
        clientId: previousRow.clientId,
        mediaUrl: message.mediaUrl ?? previousRow.mediaUrl,
        attachment: message.attachment ?? previousRow.attachment,
      };
      if (
        merged.status === previousRow.status &&
        String(merged.id) === String(previousRow.id)
      ) {
        return previousRow;
      }
      return merged;
    }

    const sameOutboundMedia =
      previousRow.role === 'user' &&
      message.role === 'user' &&
      previousRow.clientId &&
      isSameOutboundMediaUrl(previousRow, message);

    if (sameOutboundMedia) {
      const attachment = preferStableAttachment(previousRow, message);
      const text = pickStableOutgoingText(previousRow, message);
      const linkPreviews = message.linkPreviews ?? previousRow.linkPreviews;

      const mergedStatus = mergeStatus(previousRow.status, message.status);
      if (
        String(message.id) === String(previousRow.id) &&
        mergedStatus === previousRow.status &&
        attachment === previousRow.attachment &&
        text === previousRow.text &&
        linkPreviews === previousRow.linkPreviews
      ) {
        return previousRow;
      }

      return {
        ...previousRow,
        id: message.id,
        status: mergedStatus,
        clientId: previousRow.clientId,
        createdAt: previousRow.createdAt,
        attachment,
        text,
        linkPreviews,
      };
    }

    if (
      previousRow.role === 'user' &&
      previousRow.clientId &&
      previousRow.createdAt !== message.createdAt
    ) {
      return {
        ...message,
        clientId: previousRow.clientId,
        createdAt: previousRow.createdAt,
        status: mergeStatus(previousRow.status, message.status),
      };
    }

    if (!isSameMessageContent(previousRow, message)) {
      const clientId = message.clientId ?? previousRow.clientId;
      const status = mergeStatus(previousRow.status, message.status);
      if (clientId && !message.clientId) {
        return { ...message, clientId, status };
      }
      if (status !== message.status) {
        return { ...message, status };
      }
      return message;
    }

    const mergedStatus = mergeStatus(previousRow.status, message.status);
    if (mergedStatus === previousRow.status) {
      const clientId = message.clientId ?? previousRow.clientId;
      if (clientId && clientId !== previousRow.clientId) {
        return { ...previousRow, clientId };
      }
      return previousRow;
    }

    return {
      ...previousRow,
      status: mergedStatus,
      clientId: message.clientId ?? previousRow.clientId,
    };
  });
}
